import { CustomTableProps } from "./types";
import {
	TableBody,
	TableRow,
	Table,
	TableHeader,
	TableHeaderCell,
	mergeClasses,
} from "@fluentui/react-components";

import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useTableStyle } from "./style";
import { HeaderActions } from "./HeaderActions";
import { ArrowDownRegular, ArrowUpRegular } from "@fluentui/react-icons";

export const CustomTable = (props: CustomTableProps) => {
	const {
		columns,
		getRowTemplateCallback,
		columnSizing,
		headerSortProps,
		rows,
	} = props;
	const intl = useIntl();

	const classes = useTableStyle();

	const interactionTableColumnName = useCallback(
		(name: string, label?: string) =>
			name.length
				? label ??
					intl.formatMessage({
						id: `${props.headerTranslationPrefix}.${name}`,
					})
				: "",
		[intl]
	);

	const header = useCallback((): JSX.Element[] => {
		return columns.map((column, index) => {
			const showHeaderText = !props.hideHeaders && !column.hideHeader;
			const isSortable = !!props.sortable && !column.notSortable;
			const sortProps = isSortable
				? headerSortProps(column.columnId)
				: undefined;
			const isSorted = !!sortProps?.sortDirection;
			const isRemovable = !!props.columnsRemovable && !column.notRemovable;

			const label = showHeaderText
				? interactionTableColumnName(column.columnId.toString())
				: "";

			const sortTitle = intl.formatMessage({
				id: "Interaction.TableActions.Sort",
			});
			const hideColumnTitle = intl.formatMessage({
				id: "Interaction.TableActions.HideColumn",
			});

			return (
				<TableHeaderCell
					key={column.columnId}
					className={mergeClasses(
						props.classNames?.headerCell ?? "",
						props.hideHeaders ? classes.noHeaderHeaderCell : "",
						props.isStickyHeader ? classes.stickyHeader : "",
						props.resizable && !column.notResizable
							? classes.resizableHeaderCell
							: "",
						isSortable ? classes.sortableHeaderCell : "",
						isSortable || isRemovable ? classes.interactiveHeaderCell : ""
					)}
					style={{
						width: column.width,
					}}
					{...(props.resizable &&
						!column.notResizable && {
							...columnSizing.getTableHeaderCellProps(column.columnId),
						})}
					{...(sortProps ?? {})}
					sortIcon={null}
				>
					<span className={classes.headerCellInner}>
						{showHeaderText && (
							<span className={classes.headerLabelGroup}>
								<span className={classes.headerLabel}>{label}</span>
								{isSortable && isSorted && (
									<span
										aria-hidden="true"
										className={classes.sortHintIcon}
									>
										{sortProps?.sortDirection === "ascending" ? (
											<ArrowUpRegular fontSize={12} />
										) : (
											<ArrowDownRegular fontSize={12} />
										)}
									</span>
								)}
							</span>
						)}
						<HeaderActions
							showSort={showHeaderText && isSortable && !isSorted}
							showRemove={isRemovable}
							sortTitle={sortTitle}
							hideColumnTitle={hideColumnTitle}
							onRemove={() => {
								props.removeColumn ? props.removeColumn(index) : "";
							}}
						/>
					</span>
				</TableHeaderCell>
			);
		});
	}, [columns, columnSizing, headerSortProps, props, classes, intl, interactionTableColumnName]);

	const body = useMemo<JSX.Element[]>(
		() => rows.map(({ item }) => getRowTemplateCallback(item)),
		[rows, getRowTemplateCallback]
	);

	return (
		<Table
			sortable={props.sortable == null || props.sortable}
			aria-label="custom-table"
			className={mergeClasses(
				props.classNames?.tableClass ?? "",
				classes.table
			)}
			{...columnSizing.getTableProps()}
		>
			<TableHeader
				className={mergeClasses(classes.table, props.classNames?.header)}
			>
				<TableRow
					className={mergeClasses(
						classes.row,
						classes.headerText,
						props.hideHeaders ? classes.noHeaderRow : "",
						props.classNames?.headerCell ?? ""
					)}
				>
					{header()}
				</TableRow>
			</TableHeader>
			<TableBody>{body}</TableBody>
		</Table>
	);
};
